import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { processAssessment } from '../api.js';
import '../firebase-config';

const Assessment = () => {
  // Load saved state from localStorage
  const savedState = localStorage.getItem('assessmentFormData');
  const savedStep = localStorage.getItem('assessmentCurrentStep');
  
  const [currentStep, setCurrentStep] = useState(savedStep ? parseInt(savedStep) : 1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showHelpTooltip, setShowHelpTooltip] = useState({});
  const [formData, setFormData] = useState(savedState ? JSON.parse(savedState) : {
    zipCode: '',
    propertyType: '',
    features: [],
    ownership: '',
    goals: [],
    email: ''
  });

  const navigate = useNavigate();
  const totalSteps = 6;

  // Handle tooltip show/hide with auto-dismiss
  const toggleHelpTooltip = (step) => {
    setShowHelpTooltip(prev => ({
      ...prev,
      [step]: !prev[step]
    }));
    
    // Auto-dismiss after 6 seconds
    if (!showHelpTooltip[step]) {
      setTimeout(() => {
        setShowHelpTooltip(prev => ({
          ...prev,
          [step]: false
        }));
      }, 6000);
    }
  };

  // Save state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('assessmentFormData', JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    localStorage.setItem('assessmentCurrentStep', currentStep.toString());
  }, [currentStep]);

  // Handle browser back button
  useEffect(() => {
    const handlePopstate = (e) => {
      e.preventDefault();
      if (currentStep > 1) {
        setCurrentStep(prev => prev - 1);
      } else {
        if (window.confirm('Are you sure you want to leave? Your progress will be saved.')) {
          navigate('/');
        }
      }
    };

    window.addEventListener('popstate', handlePopstate);
    return () => window.removeEventListener('popstate', handlePopstate);
  }, [currentStep, navigate]);

  const [showClearProgress, setShowClearProgress] = useState(false);
  
  useEffect(() => {
    const hasProgress = Object.values(formData).some(value => 
      Array.isArray(value) ? value.length > 0 : value !== ''
    );
    setShowClearProgress(hasProgress);
  }, [formData]);

  const clearProgress = () => {
    if (window.confirm('Are you sure you want to start over? This will clear all your answers.')) {
      setFormData({
        zipCode: '',
        propertyType: '',
        features: [],
        ownership: '',
        goals: [],
        email: ''
      });
      setCurrentStep(1);
      localStorage.removeItem('assessmentFormData');
      localStorage.removeItem('assessmentCurrentStep');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: prev[name].includes(value) 
          ? prev[name].filter(item => item !== value)
          : [...prev[name], value]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      
      // Auto-advance for single-select options (radio buttons)
      if (type === 'radio' && (currentStep === 2 || currentStep === 4)) {
        // Add a longer delay for visual feedback
        setTimeout(() => {
          if (currentStep < totalSteps) {
            setCurrentStep(prev => prev + 1);
          }
        }, 600);
      }
    }
  };

  const validateCurrentStep = () => {
    switch(currentStep) {
      case 1: return formData.zipCode.trim() !== '';
      case 2: return formData.propertyType !== '';
      case 3: return formData.features.length > 0;
      case 4: return formData.ownership !== '';
      case 5: return formData.goals.length > 0;
      case 6: return formData.email !== '';
      default: return false;
    }
  };

  const showValidationWarning = () => {
    const messages = {
      1: 'Please complete your ZIP code first.',
      2: 'Please select your property type first.',
      3: 'Please select at least one property feature first.',
      4: 'Please select ownership status first.',
      5: 'Please select at least one project goal first.'
    };
    alert(messages[currentStep] || 'Please complete all previous questions first.');
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      if (validateCurrentStep()) {
        setCurrentStep(prev => prev + 1);
      } else {
        alert('Please answer this question before continuing.');
      }
    } else {
      handleSubmit();
    }
  };
  
  const validateAllSteps = () => {
    return formData.zipCode.trim() !== '' &&
           formData.propertyType !== '' &&
           formData.features.length > 0 &&
           formData.ownership !== '' &&
           formData.goals.length > 0;
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!formData.email || !formData.email.includes('@')) {
      alert('Please enter a valid email address.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Send data in the format the Cloud Function expects
      const assessmentData = {
        answers: {
          zip_code: formData.zipCode,
          property_type: formData.propertyType,
          features: formData.features,
          ownership: formData.ownership, 
          goals: formData.goals,
          email: formData.email
        },
        timestamp: new Date().toISOString()
      };
      
      const response = await processAssessment(assessmentData);
      
      if (response.publicToken) {
        // Clear saved progress after successful submission
        localStorage.removeItem('assessmentFormData');
        localStorage.removeItem('assessmentCurrentStep');
        
        // Navigate to results page with token
        navigate(`/assessment/${response.publicToken}`);
      } else {
        throw new Error('No token received from server');
      }
    } catch (error) {
      console.error('Error processing assessment:', error);
      alert('There was an error processing your assessment. Please try again.');
      setIsSubmitting(false);
    }
  };

  const progressPercent = ((currentStep - 1) / (totalSteps - 1)) * 100;

  const getStepTitle = () => {
    switch(currentStep) {
      case 1: return "What is your ZIP code?";
      case 2: return "Property Type";
      case 3: return "Property Features"; 
      case 4: return "Ownership Status";
      case 5: return "Project Goals";
      case 6: return "Get Your Results";
      default: return "ADU Feasibility Assessment";
    }
  };

  const getStepHelp = () => {
    switch(currentStep) {
      case 1: return "We use the ZIP Code to analyze local zoning laws and market conditions for your potential ADU project.";
      case 2: return "Allows us to run initial zoning and allowance ability without needing your actual project address.";
      case 3: return "Helps identify potential ADU conversion opportunities.";
      case 4: return "Checks for potential owner-occupancy requirements mandated by some localities without needing to know your specific property status.";
      case 5: return "The reason you may want or need an ADU lets us determine possible solutions; as an example, some localities allow ADUs by right if it is for a family member or caregiver.";
      case 6: return "We'll send your free analysis to your inbox, as well as display it on the next screen.";
      default: return "";
    }
  };

  return (
    <>
      <style>{`
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-in-out;
        }
        
        @keyframes fadeIn {
          from { 
            opacity: 0;
            transform: translateY(10px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @media (max-width: 640px) {
          .assessment-container {
            padding: 10px;
          }
        }
      `}</style>
      
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-5 sm:p-5 p-0">
        <div className="bg-white dark:bg-gray-800 rounded-none sm:rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 w-full max-w-4xl h-screen sm:h-[85vh] flex flex-col relative">
          <div className="px-4 sm:px-8 pt-6 sm:pt-8 pb-4 sm:pb-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Step {currentStep} of {totalSteps}
              </div>
              <div className="flex-1">
                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-600 dark:bg-blue-500 transition-all duration-400 ease-out"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
              {showClearProgress && currentStep > 1 && (
                <button 
                  onClick={clearProgress} 
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Start Over
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 px-4 sm:px-8 py-6 flex items-center justify-center overflow-y-auto pb-28 sm:pb-6">
            <form onSubmit={(e) => e.preventDefault()} noValidate className="w-full max-w-2xl">
              {/* Step 1: ZIP Code */}
              {currentStep === 1 && (
                <div className="animate-fadeIn">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-8">
                      <h2 className="text-base text-gray-500 dark:text-gray-400">ADU Feasibility Assessment</h2>
                      <button
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                        onClick={() => toggleHelpTooltip(1)}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    </div>
                    {showHelpTooltip[1] && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4 text-sm text-blue-700 dark:text-blue-300 max-w-lg mx-auto animate-fadeIn">
                        {getStepHelp()}
                      </div>
                    )}
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">What is your ZIP code?</h3>
                  </div>
                  <div className="mb-6">
                    <label htmlFor="zipCode" className="sr-only">ZIP code</label>
                    <input 
                      type="text" 
                      id="zipCode"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg text-base focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800 transition-colors duration-200"
                      placeholder="e.g., 90210"
                      aria-label="ZIP code"
                      aria-required="true"
                    />
                  </div>
                </div>
              )}

              {/* Step 2: Property Type */}
              {currentStep === 2 && (
                <div className="animate-fadeIn">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-8">
                      <h2 className="text-base text-gray-500 dark:text-gray-400">ADU Feasibility Assessment</h2>
                      <button
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                        onClick={() => toggleHelpTooltip(2)}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    </div>
                    {showHelpTooltip[2] && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4 text-sm text-blue-700 dark:text-blue-300 max-w-lg mx-auto animate-fadeIn">
                        {getStepHelp()}
                      </div>
                    )}
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Which of these best describes your property?</h3>
                  </div>
                  <div className="mb-6">
                    <div className="grid grid-cols-2 gap-3">
                      {['Single-Family', 'Two-Family', 'Three-Family', 'Multi-Family', 'Commercial', 'Other'].map((type) => {
                        const value = type.toLowerCase().replace('-', '-');
                        const isSelected = formData.propertyType === value;
                        return (
                          <label key={type} className="block w-full transition-all duration-200 cursor-pointer group hover:-translate-y-0.5 hover:shadow-md" role="radio" aria-checked={isSelected}>
                            <input 
                              type="radio" 
                              name="propertyType" 
                              value={value}
                              onChange={handleInputChange}
                              className="hidden"
                              aria-label={`Property type: ${type}`}
                            />
                            <span className={`block w-full px-4 py-3 border-2 rounded-lg font-medium text-base transition-all duration-200 ${
                              isSelected 
                                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-500 dark:border-blue-400' 
                                : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 group-hover:bg-gray-50 dark:group-hover:bg-gray-600'
                            }`}>
                              {type}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Property Features */}
              {currentStep === 3 && (
                <div className="animate-fadeIn">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-8">
                      <h2 className="text-base text-gray-500 dark:text-gray-400">ADU Feasibility Assessment</h2>
                      <button
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                        onClick={() => toggleHelpTooltip(3)}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    </div>
                    {showHelpTooltip[3] && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4 text-sm text-blue-700 dark:text-blue-300 max-w-lg mx-auto animate-fadeIn">
                        {getStepHelp()}
                      </div>
                    )}
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Select all property features that apply</h3>
                  </div>
                  <div className="mb-6">
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        'Detached Garage',
                        'Basement Space',
                        'Side Yard or Backyard',
                        'Attached Garage',
                        'In-law Studio',
                        'Existing - Built'
                      ].map((feature) => {
                        const value = feature.toLowerCase().replace(/ /g, '-');
                        const isSelected = formData.features.includes(value);
                        return (
                          <label key={feature} className="block w-full transition-all duration-200 cursor-pointer group hover:-translate-y-0.5 hover:shadow-sm" role="checkbox" aria-checked={isSelected}>
                            <input 
                              type="checkbox" 
                              name="features" 
                              value={value}
                              onChange={handleInputChange}
                              className="hidden"
                              aria-label={`Property feature: ${feature}`}
                            />
                            <span className={`block w-full px-3 py-3 border-2 rounded-lg font-medium text-base transition-all duration-200 ${isSelected ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-500 dark:border-blue-400' : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 group-hover:bg-gray-50 dark:group-hover:bg-gray-600'}`}>
                              {feature}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Ownership Status */}
              {currentStep === 4 && (
                <div className="animate-fadeIn">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-8">
                      <h2 className="text-base text-gray-500 dark:text-gray-400">ADU Feasibility Assessment</h2>
                      <button
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                        onClick={() => toggleHelpTooltip(4)}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    </div>
                    {showHelpTooltip[4] && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4 text-sm text-blue-700 dark:text-blue-300 max-w-lg mx-auto animate-fadeIn">
                        {getStepHelp()}
                      </div>
                    )}
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">What is your ownership status?</h3>
                  </div>
                  <div className="mb-6">
                    <div className="grid grid-cols-1 gap-3">
                      {[
                        'Own and Occupy',
                        'Own but Do Not Occupy',
                        'Considering Buying',
                        'Other'
                      ].map((status) => {
                        const value = status.toLowerCase().replace(/ /g, '-');
                        const isSelected = formData.ownership === value;
                        return (
                          <label key={status} className="block w-full transition-all duration-200 cursor-pointer group hover:-translate-y-0.5 hover:shadow-md" role="radio" aria-checked={isSelected}>
                            <input 
                              type="radio" 
                              name="ownership" 
                              value={value}
                              onChange={handleInputChange}
                              className="hidden"
                              aria-label={`Ownership status: ${status}`}
                            />
                            <span className={`block w-full px-4 py-3 border-2 rounded-lg font-medium text-base transition-all duration-200 ${isSelected ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-500 dark:border-blue-400' : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 group-hover:bg-gray-50 dark:group-hover:bg-gray-600'}`}>
                              {status}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 5: Project Goals */}
              {currentStep === 5 && (
                <div className="animate-fadeIn">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-8">
                      <h2 className="text-base text-gray-500 dark:text-gray-400">ADU Feasibility Assessment</h2>
                      <button
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                        onClick={() => toggleHelpTooltip(5)}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    </div>
                    {showHelpTooltip[5] && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4 text-sm text-blue-700 dark:text-blue-300 max-w-lg mx-auto animate-fadeIn">
                        {getStepHelp()}
                      </div>
                    )}
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">What are your main goals for building an ADU?</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">(Select all that apply)</p>
                  </div>
                  <div className="mb-6">
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        'Rental Income',
                        'Family/Caregiver',
                        'Aging in Place',
                        'Property Value',
                        'Home Office',
                        'Other'
                      ].map((goal) => {
                        const value = goal.toLowerCase().replace(/ /g, '-');
                        const isSelected = formData.goals.includes(value);
                        return (
                          <label key={goal} className="block w-full transition-all duration-200 cursor-pointer group hover:-translate-y-0.5 hover:shadow-md" role="checkbox" aria-checked={isSelected}>
                            <input 
                              type="checkbox" 
                              name="goals" 
                              value={value}
                              onChange={handleInputChange}
                              className="hidden"
                              aria-label={`Goal: ${goal}`}
                            />
                            <span className={`block w-full px-4 py-3 border-2 rounded-lg font-medium text-base transition-all duration-200 ${isSelected ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-500 dark:border-blue-400' : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 group-hover:bg-gray-50 dark:group-hover:bg-gray-600'}`}>
                              {goal}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 6: Email */}
              {currentStep === 6 && (
                <div className="animate-fadeIn">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-8">
                      <h2 className="text-base text-gray-500 dark:text-gray-400">ADU Feasibility Assessment</h2>
                      <button
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                        onClick={() => toggleHelpTooltip(6)}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    </div>
                    {showHelpTooltip[6] && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4 text-sm text-blue-700 dark:text-blue-300 max-w-lg mx-auto animate-fadeIn">
                        {getStepHelp()}
                      </div>
                    )}
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Where should we send your ADU feasibility assessment?</h3>
                  </div>
                  <div className="mb-6">
                    <label htmlFor="email" className="sr-only">Email address</label>
                    <input 
                      type="email" 
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg text-base focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800 transition-colors duration-200"
                      placeholder="your@email.com"
                      aria-label="Email address"
                      aria-required="true"
                      aria-describedby="email-description"
                      autoComplete="email"
                    />
                    <p id="email-description" className="text-sm text-gray-600 dark:text-gray-400 mt-3">
                      We'll send your free analysis to your inbox, as well as display it on the next screen.
                    </p>
                  </div>
                </div>
              )}
            </form>
          </div>
          
          <div className="fixed sm:static bottom-0 left-0 right-0 px-4 sm:px-8 py-4 sm:py-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 z-10 rounded-b-none sm:rounded-b-2xl">
            <div className="flex justify-between items-center">
              <button 
                type="button" 
                onClick={prevStep}
                className={`px-4 sm:px-6 py-3 sm:py-2.5 rounded-lg font-medium text-gray-600 dark:text-gray-400 transition-all hover:bg-gray-100 dark:hover:bg-gray-700 ${currentStep === 1 ? 'invisible' : ''}`}
              >
                ← Back
              </button>
              <button 
                type="button" 
                onClick={nextStep}
                disabled={isSubmitting}
                className="px-6 sm:px-6 py-3 sm:py-2.5 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Processing...' : (currentStep === totalSteps ? 'Submit' : 'Next →')}
              </button>
            </div>
          </div>
        </div>
    </div>
    </>
  );
};

export default Assessment;